/**
 * useAttributes — ViewModel for the /manage/attributes screen.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Mirrors
 * src/viewmodels/useUserTypes.ts — composes the shared usePaginatedList
 * hook for reads + owns create/update/delete mutations.
 *
 * 2026-05-17: extended with the `customDataTypes` catalogue for the
 * form modal's conditional secondary dropdown (only relevant when
 * datatype='custom').
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { AttributeDto } from '../models/attribute.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: AttributeDto lives in src/models/attribute.model.ts.
// Re-exported here so existing consumers can keep importing from the viewmodel.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { AttributeDto } from '../models/attribute.model'

export interface DatatypeRef {
  readonly id: number
  readonly name: string
}

export interface CustomDataTypeRef {
  readonly id: string
  readonly type_name: string
}

export interface CreateAttributeInput {
  readonly name: string
  readonly datatype_id: number
  readonly custom_data_type_id: string | null
  readonly description: string | null
}

export interface UpdateAttributeInput {
  readonly name?: string
  readonly datatype_id?: number
  readonly custom_data_type_id?: string | null
  readonly description?: string | null
}

export interface UseAttributesReturn {
  readonly items: AttributeDto[]
  readonly datatypes: DatatypeRef[]
  readonly customDataTypes: CustomDataTypeRef[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateAttributeInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateAttributeInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListExtras {
  readonly datatypes: DatatypeRef[]
  readonly customDataTypes: CustomDataTypeRef[]
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: AttributeDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly datatypes: DatatypeRef[]
    readonly customDataTypes: CustomDataTypeRef[]
  }
}

export function useAttributes(): UseAttributesReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<AttributeDto, ListExtras>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGEATTRIBUTES}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: {
            datatypes: body.data.datatypes,
            customDataTypes: body.data.customDataTypes
          }
        }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateAttributeInput) => {
      const r = await mutateJson<AttributeDto>(
        authedFetch,
        APIROUTES.MANAGEATTRIBUTES,
        'POST',
        input
      )
      if (!r.ok) return r
      // Page-1 insert per SOP 02. List sorts DESC created_at, so the
      // new row surfaces on page 1; if the operator is on page > 1
      // they'll need to navigate back to see it (acceptable trade-off
      // matching the locked rule).
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateAttributeInput) => {
      const r = await mutateJson<AttributeDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGEATTRIBUTEITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEATTRIBUTEITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    datatypes: paged.extras?.datatypes ?? [],
    customDataTypes: paged.extras?.customDataTypes ?? [],
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
